import { useState, useEffect, useMemo } from 'react'

// Manages selection state for the two "what is this donation for" choices
// on the start panel: which partner_item (when the fundraiser splits across
// multiple beneficiary orgs) and which project (when the goal_partner_item
// has fundraiser_projects). Mirrors v6's useBeneficiarySelect: one chooser
// at most is rendered (org chooser if multi-org, project chooser otherwise),
// preselection auto-fills single options, and validate() surfaces errors
// for the start step's submit handler.
//
// init_multi_orgs in donations_controller normalizes partner_items to either
// 0 (single-org case, no chooser) or 2+ (multi-org case, chooser). The
// `partner_items.length == 1` defensive branch in v6 is preserved here for
// safety even though it's not expected to fire.
const useBeneficiarySelect = ({
  i18n, partner_item, partner_items, goal_partner_item, disable_donation_split,
}) => {
  const [ partnerItem, setPartnerItem ] = useState(() =>
    partner_items.length == 1 ? partner_items[0] : partner_item
  )
  const [ partnerItemError, setPartnerItemError ] = useState(null)

  const preselectedProject = useMemo(() =>
    goal_partner_item.projects.find(project => project.preselected) || (
      goal_partner_item.projects.length == 1
        ? goal_partner_item.projects[0]
        : null
    )
  , [ goal_partner_item.projects ])
  const [ projectId, setProjectId ] = useState(() => preselectedProject?.id || '')
  const [ projectError, setProjectError ] = useState(null)

  // Resolved project object — exposed so OrderSummary etc. can show the
  // chosen designation by name without re-deriving from the projects array.
  const project = useMemo(() =>
    goal_partner_item.projects.find(p => p.id == projectId) || null
  , [ goal_partner_item.projects, projectId ])

  // Clear errors as soon as the user picks something different.
  useEffect(() => { setPartnerItemError(null) }, [ partnerItem.external_id ])
  useEffect(() => { setProjectError(null) }, [ projectId ])

  const validate = () => {
    // The "split among nonprofits" pseudo-option resolves to the parent
    // (goal_partner_item, beneficiary.id == '_multiple'). When the partner
    // disables splits, that pseudo-option is invalid.
    const partnerItemInvalid =
      disable_donation_split && partnerItem.beneficiary.id == '_multiple'
    setPartnerItemError(partnerItemInvalid ? i18n.t('errors.blank') : null)

    const projectInvalid = goal_partner_item.projects.length && !projectId
    setProjectError(projectInvalid ? i18n.t('errors.blank') : null)

    return !partnerItemInvalid && !projectInvalid
  }

  // The two "is this fundraiser's impact statement unambiguous" flags that
  // Amount / DonationLine gate their impact line on. Both describe the
  // fundraiser's configuration, not the donor's current selection, which
  // is what makes them match the editor's gate in PWCAmounts:
  //
  //   multiOrg     mirrors `organizations.length > 1` there. init_multi_orgs
  //                normalizes partner_items to 0 or 2+, and _config.erb
  //                derives its own `multi_org` the same way. Deliberately
  //                NOT `partnerItem.beneficiary.id == '_multiple'`, which
  //                is only the "split among all" pseudo-option and goes
  //                false the moment the donor picks one org off the list.
  //   multiProject mirrors the designation count there. Deliberately not
  //                `project`, which a sole designation preselects below
  //                without ever showing the donor a chooser.
  //
  // The two multiOrg counts are the same rows one dereference apart, not
  // the same number: `partner_items_for_fundraiser` is
  // `primary_or_self.children.active`, and the editor's `organizations`
  // maps those children's beneficiary_ids back to Organization rows. Two
  // children sharing one org, or one whose org was hard-deleted, collapse
  // on the editor side only — it counts 1 and shows the control, we count
  // 2 and hide the line. Both are rare, and that's the safe direction to
  // disagree in: the organizer writes text that doesn't render, rather
  // than a donor seeing an impact claim for a beneficiary it isn't about.
  const multiOrg = partner_items.length > 1
  const multiProject = goal_partner_item.projects.length > 1

  // Mutual exclusion mirrors v6: when there are multiple orgs to choose from,
  // we surface the org chooser only; otherwise (single org, multiple projects)
  // we surface the project chooser. Project chooser stays hidden when the
  // goal has 0 or 1 project — single-project case is auto-selected silently.
  const showPartnerItemSelect = partner_items.length > 0
  const showProjectSelect = !showPartnerItemSelect && multiProject
  const projectPrompt = !preselectedProject

  return {
    partnerItem, setPartnerItem, partnerItemError,
    projectId, project, setProjectId, projectError, multiOrg, multiProject,
    showPartnerItemSelect, showProjectSelect, projectPrompt,
    validate,
  }
}

export default useBeneficiarySelect
